import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Plus, Search, MessageSquare, Bug, Lightbulb, HelpCircle, MoreHorizontal, ArrowRight, Paperclip, Upload, X, Archive, ArchiveRestore } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const STATUS_META = {
  aperta:         { label: "Aperta",         cls: "bg-amber-50 text-amber-800 border-amber-200" },
  in_lavorazione: { label: "In lavorazione", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  risolta:        { label: "Risolta",        cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  chiusa:         { label: "Chiusa",         cls: "bg-stone-100 text-stone-700 border-stone-200" },
};
const PRIORITY_META = {
  bassa:   { label: "Bassa",   cls: "bg-stone-100 text-stone-700 border-stone-200" },
  media:   { label: "Media",   cls: "bg-blue-50 text-blue-800 border-blue-200" },
  alta:    { label: "Alta",    cls: "bg-orange-50 text-orange-800 border-orange-200" },
  urgente: { label: "Urgente", cls: "bg-red-50 text-red-800 border-red-200" },
};
const CATEGORY_META = {
  bug:      { label: "Bug",         icon: Bug,         color: "#dc2626" },
  feature:  { label: "Suggerimento",icon: Lightbulb,   color: "#7c3aed" },
  question: { label: "Domanda",     icon: HelpCircle,  color: "#0369a1" },
  other:    { label: "Altro",       icon: MoreHorizontal, color: "#78716c" },
};

export default function HelpDesk() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = ["admin", "direzione", "segreteria"].includes(user?.role);
  const [filters, setFilters] = useState({ status: "all", priority: "all", q: "", archived: false, mine: false });
  const [newOpen, setNewOpen] = useState(false);

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["helpdesk-tickets", filters],
    queryFn: async () => {
      const p = {};
      if (filters.status !== "all") p.status = filters.status;
      if (filters.priority !== "all") p.priority = filters.priority;
      if (filters.q) p.q = filters.q;
      if (filters.archived) p.archived = true;
      if (filters.mine) p.mine = true;
      return (await api.get("/helpdesk/tickets", { params: p })).data;
    },
  });
  const { data: stats } = useQuery({
    queryKey: ["helpdesk-stats"],
    queryFn: async () => (await api.get("/helpdesk/stats")).data,
  });
  const archiveMut = useMutation({
    mutationFn: async ({ id, archived }) => (await api.post(`/helpdesk/tickets/${id}/archive`, null, { params: { archived } })).data,
    onSuccess: (_, v) => {
      toast.success(v.archived ? "Segnalazione archiviata" : "Segnalazione ripristinata");
      qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
      qc.invalidateQueries({ queryKey: ["helpdesk-stats"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="helpdesk-page">
      <PageHeader
        title="Help Desk"
        subtitle={isManager ? "Segnalazioni e richieste di supporto sul funzionamento della piattaforma." : "Apri una segnalazione se qualcosa non funziona o se hai una domanda."}
      />

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Totale" value={stats?.total ?? 0} />
        <Kpi label="Aperte" value={stats?.aperta ?? 0} tone="amber"/>
        <Kpi label="In lavorazione" value={stats?.in_lavorazione ?? 0} tone="sky"/>
        <Kpi label="Chiuse" value={(stats?.risolta ?? 0) + (stats?.chiusa ?? 0)} tone="stone"/>
      </div>

      <div className="inline-flex rounded-full border border-stone-200 p-1 mb-4 bg-white" data-testid="helpdesk-archive-tabs">
        {[[false, "Attive"], [true, `Archiviate (${stats?.archiviate ?? 0})`]].map(([v, l]) => (
          <button key={String(v)} onClick={() => setFilters({ ...filters, archived: v })} data-testid={`helpdesk-tab-${v ? "archived" : "active"}`}
            className={`px-4 h-8 rounded-full text-xs font-medium transition-colors duration-150 ${filters.archived === v ? "bg-[#1C352D] text-white" : "text-stone-600 hover:bg-stone-100"}`}>{l}</button>
        ))}
        {isManager && (
          <button onClick={() => setFilters({ ...filters, mine: !filters.mine })} data-testid="helpdesk-tab-mine"
            className={`px-4 h-8 rounded-full text-xs font-medium transition-colors duration-150 ${filters.mine ? "bg-[#E2725B] text-white" : "text-stone-600 hover:bg-stone-100"}`}>Assegnate a me</button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-widest">Ricerca</Label>
          <div className="relative mt-1">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Titolo o descrizione…" className="pl-7" data-testid="helpdesk-search"/>
          </div>
        </div>
        <div className="w-40">
          <Label className="text-[10px] uppercase tracking-widest">Stato</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="mt-1" data-testid="helpdesk-filter-status"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="text-[10px] uppercase tracking-widest">Priorità</Label>
          <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
            <SelectTrigger className="mt-1" data-testid="helpdesk-filter-priority"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="new-ticket-btn" className="bg-[#1C352D] hover:bg-[#264A3E]">
          <Plus size={14} className="mr-1"/> Nuova segnalazione
        </Button>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        {isLoading ? (
          <div className="p-10 text-center text-stone-400 text-sm">Caricamento…</div>
        ) : tickets.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">
            <LifeBuoy size={36} className="mx-auto mb-3 text-stone-300"/>
            {filters.archived ? "Nessuna segnalazione archiviata." : "Nessuna segnalazione presente."}
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {tickets.map((t) => {
              const CatIcon = CATEGORY_META[t.category]?.icon || HelpCircle;
              const catColor = CATEGORY_META[t.category]?.color || "#78716c";
              return (
                <li key={t.id} data-testid={`ticket-row-${t.id}`}>
                  <Link to={`/helpdesk/${t.id}`} className="flex items-start gap-4 p-4 hover:bg-stone-50 transition-colors duration-150">
                    <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ background: `${catColor}18`, color: catColor }}>
                      <CatIcon size={16}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#1C352D] truncate flex items-center gap-2 flex-wrap">
                        {t.title}
                        <Badge className={`text-[10px] rounded-full border ${STATUS_META[t.status]?.cls}`}>{STATUS_META[t.status]?.label}</Badge>
                        <Badge className={`text-[10px] rounded-full border ${PRIORITY_META[t.priority]?.cls}`}>{PRIORITY_META[t.priority]?.label}</Badge>
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5 flex flex-wrap gap-x-2">
                        <span>#{t.id.slice(0, 8).toUpperCase()}</span>
                        <span>· da <b className="text-stone-700">{t.created_by_name || "—"}</b></span>
                        <span>· {fmtDate(t.created_at)}</span>
                        <span data-testid={`ticket-assignee-${t.id}`}>· {t.assigned_to_name ? <>assegnata a <b className="text-[#1C352D]">{t.assigned_to_name}</b></> : <i>non assegnata</i>}</span>
                        {t.comments_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-stone-600">· <MessageSquare size={10}/> {t.comments_count}</span>
                        )}
                      </div>
                    </div>
                    {isManager && ["risolta", "chiusa"].includes(t.status) && (
                      <button
                        onClick={(e) => { e.preventDefault(); archiveMut.mutate({ id: t.id, archived: !t.archived }); }}
                        data-testid={`ticket-archive-${t.id}`}
                        className="self-center shrink-0 inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 h-8 text-[11px] text-stone-600 hover:bg-[#1C352D] hover:text-white hover:border-[#1C352D] transition-colors duration-150"
                      >
                        {t.archived ? <><ArchiveRestore size={12}/> Ripristina</> : <><Archive size={12}/> Archivia</>}
                      </button>
                    )}
                    <ArrowRight size={16} className="text-stone-300 shrink-0 self-center"/>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {newOpen && (
        <NewTicketDialog
          onClose={() => setNewOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["helpdesk-tickets"] });
            qc.invalidateQueries({ queryKey: ["helpdesk-stats"] });
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "default" }) {
  const toneCls = {
    default: "text-[#1C352D]",
    amber: "text-amber-700",
    sky: "text-sky-700",
    stone: "text-stone-700",
  }[tone];
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneCls}`}>{value}</div>
    </Card>
  );
}

function NewTicketDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", category: "bug", priority: "media" });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim() || form.title.trim().length < 3) { toast.error("Titolo troppo breve"); return; }
    if (!form.description.trim() || form.description.trim().length < 3) { toast.error("Descrizione troppo breve"); return; }
    setSaving(true);
    try {
      const created = (await api.post("/helpdesk/tickets", form)).data;
      // Upload sequenziale allegati scelti
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/helpdesk/tickets/${created.id}/attachments`, fd);
      }
      toast.success(files.length ? `Segnalazione aperta con ${files.length} allegato${files.length > 1 ? "i" : ""}` : "Segnalazione aperta");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  function addFiles(fileList) {
    const arr = Array.from(fileList || []);
    const max = 15 * 1024 * 1024;
    const valid = arr.filter((f) => {
      if (f.size > max) { toast.error(`${f.name}: file troppo grande (max 15 MB)`); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...valid]);
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 flex flex-col gap-0" data-testid="new-ticket-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-stone-100 shrink-0">
          <DialogTitle className="flex items-center gap-2"><LifeBuoy size={16}/> Nuova segnalazione</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4">
          <div>
            <Label>Titolo *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="Es. Non riesco a stampare il PDF studenti" data-testid="ticket-title"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipologia</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="ticket-category"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorità</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1" data-testid="ticket-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Descrizione *</Label>
            <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" placeholder="Spiega il problema, cosa ti aspettavi, cosa è successo e in quale schermata…" data-testid="ticket-description"/>
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Paperclip size={12}/> Allegati</Label>
            <label className="cursor-pointer mt-1 inline-flex items-center gap-2 rounded-full border border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white px-3 h-9 text-[12px] transition-colors duration-150">
              <Upload size={13}/> Aggiungi file
              <input type="file" className="hidden" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.txt,.log,.csv,.zip" onChange={(e) => addFiles(e.target.files)} data-testid="ticket-attach-pre"/>
            </label>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 bg-stone-50 border border-stone-200 rounded-md px-2 py-1.5 text-[12px]">
                    <span className="truncate flex-1"><b className="text-stone-800">{f.name}</b> <span className="text-stone-500">· {(f.size / 1024).toFixed(0)} KB</span></span>
                    <button onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))} className="text-stone-400 hover:text-red-600 p-0.5"><X size={13}/></button>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-[10px] text-stone-500 mt-1">PDF, immagini, log, CSV, ZIP · max 15 MB per file</div>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-stone-100 bg-white shrink-0">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="ticket-save" className="bg-[#1C352D] hover:bg-[#264A3E]">
            {saving ? "Invio…" : "Invia segnalazione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
